import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, Icon, Pagination } from "semantic-ui-react";
import LeaveManagementTable from "../../components/LeaveManagementTable";
import LeaveSearchConditionsForm from "../../components/LeaveSearchConditionForm";
import { getAllLeaveRequest } from "../../core/actions/allLeaveRequestAction";
import { exportFileAction } from "../../core/actions/commonAction";
import { clearLoadingHistory } from "../../core/actions/leaveHistoryAction";
import { getLeaveTypeList } from "../../core/actions/myLeaveRequestAction";
import { getDataDepartmentAndEmployee } from "../../core/actions/totalRecordByDateAction";
import functionCommon from "../../core/common/function";
import { employeeStatus } from "../../core/constant/allLeaveRequest.constants";
import "./styles.scss";

const initParams = {
    leaveType: { sortBy: "id-ASC" },
    allLeave: {
        isSearch: 0,
        dayFrom: moment().startOf("month").format("DD-MM-YYYY"),
        dayTo: moment().format("DD-MM-YYYY"),
        leaveTypeId: "",
        departmentId: "",
        approverId: "",
        requestStatus: "",
        employeeId: "",
        employeeStatus: "",
        sortBy: "firstName-ASC",
        pageRecord: 20,
    },
    department: {
        departmentId: "",
        departmentBy: "createdDate-ASC",
        employeeBy: "firstName-ASC",
    },
};

export default function AllLeaveRequest() {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const leaveType = useSelector((state) => state.myLeaveRequestReducer.listLeaveType);
    const department = useSelector((state) => state.departmentAndEmployeeReducer);
    const { searching, allLeaveRequest, isLoading } = useSelector((state) => state.allLeaveRequestReducer);
    const [conditions, setConditions] = useState(initParams.allLeave);
    const [currentPage, setCurrentPage] = useState(1);
    const [message, setMessage] = useState(null);
    const conditionRef = useRef(initParams.allLeave);
    const [firstCall, setFirstCall] = useState(true);
    const [dataError, setDataError] = useState({
        day: null,
    });

    const handleGoToAdd = () => {
        history.push(`/my-leave-request/new-leave-request`);
    };

    const gotoLeaveHistory = (employeeId) => () => {
        const queryParam = functionCommon.parseQuery({
            dayFrom: conditionRef.current.dayFrom,
            dayTo: conditionRef.current.dayTo,
            leaveTypeId: conditionRef.current.leaveTypeId,
            approverId: conditionRef.current.approverId,
            requestStatus: conditionRef.current.requestStatus,
            employeeId: employeeId,
        });
        dispatch(clearLoadingHistory());
        history.push(`/all-leave-request/leave-history?${queryParam}`);
    };

    const leaveTypeProcess = (rawData) => {
        if (rawData && rawData.length > 0) {
            const leaveTypes = [];
            rawData.forEach((ele) => {
                leaveTypes.push({
                    value: ele.leaveTypeId,
                    name: ele.leaveTypeName,
                });
            });
            return leaveTypes;
        } else return [];
    };

    const departmentProcess = (rawData) => {
        if (rawData && rawData.length > 0) {
            const department = [];
            rawData.forEach((ele) => {
                department.push({
                    value: ele.departmentId,
                    name: ele.departmentName,
                });
            });
            return department;
        } else return [];
    };

    const employeeProcess = (rawData) => {
        if (rawData && rawData.length > 0) {
            const employee = [];
            rawData.forEach((ele) => {
                employee.push({
                    value: ele.employeeId,
                    name: ele.employeeCode + " - " + ele.employeeName,
                });
            });
            return employee;
        } else return [];
    };

    const onChangeConditions = (value) => {
        setConditions(value);
    };

    const handleSearch = () => {
        if (validateField()) {
            dispatch(getAllLeaveRequest({ ...conditions, isSearch: 1, currentPage: 1 }, true));
            conditionRef.current = { ...conditions, isSearch: 1 };
            setCurrentPage(1);
        }
    };

    const handlePaginationChange = (val) => {
        setCurrentPage(val);
        dispatch(getAllLeaveRequest({ ...conditionRef.current, currentPage: val }, false, true));
    };

    const triggerMessage = () => {
        setTimeout(() => {
            setMessage(null);
        }, 2000);
    };

    const downloadInstruction = () => {
        dispatch(
            exportFileAction(
                {
                    urlGetkey: "Leave/requestSecretKey",
                    urlDownfile: "Leave/downloadInstruction",
                },
                { secretKey: "" }
            )
        );
    };

    const validateField = () => {
        const error = {};
        const formatDayFrom = conditions.dayFrom;
        const formatDayTo = conditions.dayTo;
        const dayFrom = new Date(moment(formatDayFrom, "DDMMYYYY ").local());
        const dayTo = new Date(moment(formatDayTo, "DDMMYYYY ").local());

        if (dayFrom > dayTo) {
            error.day = "message.040";
        } else {
            error.day = null;
        }
        setDataError(error);
        return !error.day;
    };

    useEffect(() => {
        dispatch(getLeaveTypeList(initParams.leaveType));
        dispatch(getDataDepartmentAndEmployee(initParams.department));
        dispatch(getAllLeaveRequest({ ...initParams.allLeave, currentPage: 1 }));
        setFirstCall(false);
    }, []);

    useEffect(() => {
        if (!firstCall) {
            dispatch(getDataDepartmentAndEmployee({ ...initParams.department, departmentId: conditions.departmentId }));
            setConditions({
                ...conditions,
                employeeId: "",
                approverId: "",
            });
        }
    }, [conditions.departmentId]);

    const dataTable = useMemo(() => {
        const result = {
            header: [
                t("labels.fullNameSpace"),
                t("labels.staffCode"),
                t("labels.employeeStatusLeave"),
                t("labels.approver"),
                t("labels.days"),
                t("labels.hours"),
            ],
        };
        const body = [];
        if (allLeaveRequest.getAllLeaveRequest && allLeaveRequest.getAllLeaveRequest.length > 0) {
            allLeaveRequest.getAllLeaveRequest.forEach((ele, idx) => {
                body.push([
                    <a className="leave-active-link" onClick={gotoLeaveHistory(ele.employeeId)}>
                        {ele.employeeName}
                    </a>,
                    ele.employeeCode,
                    t("labels." + employeeStatus[ele.employeeStatus]),
                    ele.approverForLast,
                    ele.totalDaysRequest,
                    Number(ele.totalHoursRequest),
                ]);
            });
            result.body = body;
            setMessage(null);
            return result;
        } else {
            setMessage(t("message.018"));
            triggerMessage();
            return result;
        }
    }, [allLeaveRequest, t]);

    useEffect(() => {
        const error = {};
        setDataError(error);
    }, [conditions.dayFrom, conditions.dayTo]);

    return (
        <div className="all-leave-request">
            <h2 className="page-heading">
                <span onClick={functionCommon.goToPage("/my-leave-request")}>{t("labels.leaveManagemnet")}</span>
                {"/ "}
                <span onClick={functionCommon.goToPage("/all-leave-request")}>{t("labels.allLeaveRequest")}</span>
            </h2>
            <div className="btn-group">
                <Button className="leave-button left" onClick={handleGoToAdd}>
                    {t("button.newLeaveRequest")}
                </Button>
                <Button className="leave-button" onClick={downloadInstruction}>
                    {t("labels.instruction")}
                </Button>
            </div>
            <div className="search-condition-wrapper">
                <p className="leave-title">{t("labels.searchConditions")}</p>
                <LeaveSearchConditionsForm
                    listLeaveType={leaveTypeProcess(leaveType)}
                    listDepartment={departmentProcess(department.listDepartmentSelectBox)}
                    listEmployee={employeeProcess(department.listEmployeeSelectBox)}
                    listApprover={employeeProcess(department.listApproverSelectBox)}
                    conditions={conditions}
                    onChangeConditions={onChangeConditions}
                    handleSearch={handleSearch}
                    isSearching={searching}
                    dataError={dataError}
                />
            </div>
            <div className="summary-table">
                {message && <p className="error-message list-error-message">{message}</p>}
                <p className="leave-title">{t("labels.staffRequestedTime")}</p>
                <LeaveManagementTable content={dataTable} customTableClass="table-custom" />
                {allLeaveRequest.totalRecord > 0 && (
                    <div className="leave-table-paging">
                        <Pagination
                            size="tiny"
                            activePage={currentPage}
                            firstItem={null}
                            lastItem={null}
                            disabled={isLoading}
                            totalPages={allLeaveRequest.totalPage > 0 ? allLeaveRequest.totalPage : null}
                            prevItem={{
                                disabled: currentPage === 1 || isLoading,
                                content: <Icon name="chevron left" />,
                                icon: true,
                            }}
                            nextItem={{
                                disabled: currentPage === allLeaveRequest.totalPage || isLoading,
                                content: <Icon name="chevron right" />,
                                icon: true,
                            }}
                            onPageChange={(e, { activePage }) => handlePaginationChange(activePage)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
